/**
 * Sesli gorusme - telefonda konusur gibi, dugmeye basmadan.
 *
 * Dongu: dinle -> (konusma bitti) -> metne cevir -> sohbete gonder -> yaniti
 * CUMLE CUMLE seslendir -> tekrar dinle. Kullanici yanit okunurken konusmaya
 * baslarsa okuma susuyor, uretim iptal ediliyor ve onu dinliyoruz (kullanici
 * karari: "susup sizi dinlesin").
 *
 * ## Neden tarayici API'lerinden BAGIMSIZ
 *
 * Mikrofon, kayit, oynatma ve sunucu cagrilari `CallDeps` ile veriliyor.
 * Boylece donguyu (asil zor kisim: turlar, soz kesme, siralama) sahte
 * bagimliliklarla ve sahte zamanlayicilarla test edebiliyoruz; jsdom'da
 * `MediaRecorder` ve `AudioContext` yok.
 *
 * ## Gecikme nereden geliyor
 *
 * Sessizlik algilama (~0,9 sn) + metne cevirme (~1,2 sn) + ilk cumle
 * (~1 sn, hizli model) + seslendirme (~0,8 sn). Yanit BITMEDEN ilk cumle
 * okunmaya basliyor: tum yaniti beklemek uzun yanitlarda 10+ saniye
 * sessizlik demekti.
 */

import { toSegments } from './segments'
import { SentenceSplitter } from './sentences'
import { micErrorKey } from './speech'
import { splitThinking } from './think'

export type CallState =
  | 'starting'
  | 'listening'
  | 'hearing'
  | 'transcribing'
  | 'thinking'
  | 'speaking'
  | 'ended'

export interface RecorderHandle {
  stop(): Promise<Blob>
  cancel(): void
}

export interface MicHandle {
  /** Anlik ses seviyesi (RMS, 0-1). */
  level(): number
  record(): RecorderHandle
  close(): void
}

export interface PlayerHandle {
  /** Calar; bitince VEYA `stop()` ile kesilince cozulur. */
  play(audio: Blob): Promise<void>
  stop(): void
}

export interface RunView {
  text: string
  done: boolean
  failed: boolean
}

export interface CallDeps {
  openMic(): Promise<MicHandle>
  player: PlayerHandle
  upload(audio: Blob): Promise<{ id: string; transcript: string }>
  /** Mesaji gonderir, run kimligini dondurur. */
  submit(text: string, attachmentId: string): Promise<string>
  readRun(runId: string): RunView
  watchRun(runId: string, onChange: () => void): () => void
  cancelRun(runId: string): Promise<void>
  tts(text: string, lang: string | null): Promise<{ audio: Blob; lang: string | null }>
}

export interface CallSnapshot {
  state: CallState
  muted: boolean
  level: number
  /** Son soylenenin transkripti. */
  heard: string
  /** Su anki yanitin (seslendirilebilir) metni. */
  reply: string
  /** Gosterilecek hata - ceviri ANAHTARI. */
  error: string | null
}

export const TICK_MS = 50
/** Hic konusulmadan bu kadar dinlendiyse kayit tazeleniyor (dosya sismesin). */
const SILENCE_RESET_MS = 20_000
/** Tek bir soz en fazla bu kadar: sunucu siniri 120 sn. */
const MAX_UTTERANCE_MS = 60_000

// ------------------------------------------------------------ konusma algilama

/**
 * Enerji tabanli konusma algilayici.
 *
 * Model (Silero vb.) DEGIL: sayfanin CSP'si harici betik ve `blob:` worker'a
 * kapali, ustelik mikrofon kaydi zaten tarayicinin gurultu bastirmasindan
 * geciyor. Esik ORTAM GURULTUSUNE uyarlaniyor: sessiz odada 0,02, klimali
 * bir odada daha yuksek.
 *
 * Soz kesme (yanit okunurken) icin esik ve sure DAHA YUKSEK: hoparlorden
 * mikrofona kacan yanit sesi (yanki giderme her zaman kusursuz degil)
 * kullanicinin sozu sanilmasin.
 */
export class VoiceActivity {
  private floor = 0.008
  private speaking = false
  private above = 0
  private below = 0
  private speech = 0

  reset(): void {
    this.speaking = false
    this.above = 0
    this.below = 0
    this.speech = 0
  }

  threshold(bargeIn = false): number {
    const base = Math.max(this.floor * 3, 0.02)
    return bargeIn ? Math.max(base * 2.5, 0.06) : base
  }

  push(level: number, dt: number, { bargeIn = false } = {}): 'start' | 'end' | null {
    const threshold = this.threshold(bargeIn)
    if (!this.speaking) {
      if (level > threshold) {
        this.above += dt
        if (this.above >= (bargeIn ? 300 : 200)) {
          this.speaking = true
          this.speech = this.above
          this.below = 0
          return 'start'
        }
      } else {
        this.above = 0
        // Gurultu tabani yalnizca SESSIZKEN ogreniliyor; yanit okunurken
        // hoparlor sesi tabani yukseltmesin.
        if (!bargeIn) this.floor = Math.min(0.05, this.floor * 0.95 + level * 0.05)
      }
      return null
    }
    this.speech += dt
    if (level < threshold * 0.75) {
      this.below += dt
      // 0,9 sn sessizlik = soz bitti. Daha kisa, "ee..." duraklamalarini
      // sozun sonu sanip cumleyi ikiye boluyordu.
      if (this.below >= 900 && this.speech >= 400) {
        this.reset()
        return 'end'
      }
    } else {
      this.below = 0
    }
    return null
  }
}

// Cumle bolucu ayri modulde: "sesli oku" dugmesi de kullaniyor (bkz. `speech.ts`).
export { SentenceSplitter }

/**
 * Akis metninden kullanicinin gordugu yanit: dusunme blogu ve arac
 * kartlari cikarilmis. Arac karti metni okunacak bir sey degil.
 */
export function speakableAnswer(full: string): string {
  const { answer } = splitThinking(full)
  return toSegments(answer)
    .filter((segment) => segment.kind === 'md')
    .map((segment) => (segment as { text: string }).text)
    .join('\n')
}

// ------------------------------------------------------------ gorusme

export class CallSession {
  private mic: MicHandle | null = null
  private recorder: RecorderHandle | null = null
  private readonly vad = new VoiceActivity()
  private timer: ReturnType<typeof setInterval> | null = null
  /** Her yeni turda artiyor; eski turun gec gelen sonuclari yok sayiliyor. */
  private turn = 0
  private runId: string | null = null
  private runDone = false
  private unwatch: (() => void) | null = null
  private splitter = new SentenceSplitter()
  private consumed = 0
  private chain: Promise<unknown> = Promise.resolve()
  private audio: Promise<Blob | null>[] = []
  private pumping = false
  private lang: string | null = null
  private listenMs = 0
  private hearMs = 0
  private snap: CallSnapshot = {
    state: 'starting',
    muted: false,
    level: 0,
    heard: '',
    reply: '',
    error: null,
  }

  private readonly deps: CallDeps
  private readonly onChange: (snapshot: CallSnapshot) => void

  // Alanlar govdede: `erasableSyntaxOnly` parametre ozelliklerine izin vermiyor.
  constructor(deps: CallDeps, onChange: (snapshot: CallSnapshot) => void) {
    this.deps = deps
    this.onChange = onChange
  }

  get snapshot(): CallSnapshot {
    return this.snap
  }

  async start(): Promise<void> {
    try {
      const mic = await this.deps.openMic()
      if (this.snap.state === 'ended') {
        mic.close()
        return
      }
      this.mic = mic
    } catch (cause) {
      this.set({ state: 'ended', error: micErrorKey(cause) })
      return
    }
    this.timer = setInterval(() => this.tick(), TICK_MS)
    this.listen()
  }

  end(): void {
    this.turn++
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    this.recorder?.cancel()
    this.recorder = null
    this.unwatch?.()
    this.unwatch = null
    this.audio = []
    this.deps.player.stop()
    this.mic?.close()
    this.mic = null
    this.set({ state: 'ended', level: 0 })
  }

  toggleMute(): void {
    if (this.snap.state === 'ended') return
    const muted = !this.snap.muted
    if (muted) {
      this.recorder?.cancel()
      this.recorder = null
      this.vad.reset()
      this.set({ muted, state: this.snap.state === 'hearing' ? 'listening' : this.snap.state })
      return
    }
    this.set({ muted })
    if (this.snap.state === 'listening') this.startRecorder()
  }

  /** "Sozum bitti": gurultulu ortamda sessizlik algilanamazsa. */
  finishTurn(): void {
    if (this.snap.state === 'hearing') void this.endUtterance()
  }

  // --------------------------------------------------------------- ic isleyis

  private set(patch: Partial<CallSnapshot>): void {
    this.snap = { ...this.snap, ...patch }
    this.onChange(this.snap)
  }

  private listen(): void {
    this.turn++
    this.runId = null
    this.chain = Promise.resolve()
    this.vad.reset()
    this.hearMs = 0
    this.set({ state: 'listening' })
    if (!this.snap.muted) this.startRecorder()
  }

  private startRecorder(): void {
    this.recorder?.cancel()
    this.recorder = this.mic?.record() ?? null
    this.listenMs = 0
  }

  private tick(): void {
    if (!this.mic) return
    const level = this.mic.level()
    this.set({ level })
    if (this.snap.muted) return
    const state = this.snap.state

    if (state === 'listening' || state === 'hearing') {
      const event = this.vad.push(level, TICK_MS)
      if (event === 'start') this.set({ state: 'hearing', error: null })
      if (this.snap.state === 'hearing') {
        this.hearMs += TICK_MS
      } else {
        this.listenMs += TICK_MS
        if (this.listenMs > SILENCE_RESET_MS) this.startRecorder()
      }
      if (event === 'end' || this.hearMs > MAX_UTTERANCE_MS) void this.endUtterance()
      return
    }

    if (state === 'thinking' || state === 'speaking') {
      if (this.vad.push(level, TICK_MS, { bargeIn: true }) === 'start') this.interrupt()
    }
  }

  /** Kullanici yanitin USTUNE konustu: sus, iptal et, onu dinle. */
  private interrupt(): void {
    const runId = this.runId
    this.turn++
    this.audio = []
    this.chain = Promise.resolve()
    this.deps.player.stop()
    this.unwatch?.()
    this.unwatch = null
    // Uretim de iptal: aksi halde sunucu ayni konusmada "zaten bir yanit
    // uretiliyor" (409) der ve kullanicinin yeni sozu bekler.
    if (runId && !this.deps.readRun(runId).done) void this.deps.cancelRun(runId)
    this.runId = null
    this.hearMs = 0
    this.recorder?.cancel()
    this.recorder = this.mic?.record() ?? null
    this.set({ state: 'hearing', reply: '' })
  }

  private async endUtterance(): Promise<void> {
    const recorder = this.recorder
    this.recorder = null
    if (!recorder) return
    const turn = ++this.turn
    this.vad.reset()
    this.set({ state: 'transcribing' })
    try {
      const recording = await recorder.stop()
      const voice = await this.deps.upload(recording)
      if (turn !== this.turn) return
      if (!voice.transcript) {
        this.set({ error: 'call.notUnderstood' })
        this.listen()
        return
      }
      this.set({ state: 'thinking', heard: voice.transcript, reply: '', error: null })
      const runId = await this.deps.submit(voice.transcript, voice.id)
      if (turn !== this.turn) return
      this.follow(runId, turn)
    } catch {
      if (turn !== this.turn) return
      this.set({ error: 'call.failed' })
      this.listen()
    }
  }

  private follow(runId: string, turn: number): void {
    this.runId = runId
    this.runDone = false
    this.consumed = 0
    this.lang = null
    this.splitter = new SentenceSplitter()
    const update = () => {
      if (turn !== this.turn) return
      const view = this.deps.readRun(runId)
      const answer = speakableAnswer(view.text)
      if (answer.length > this.consumed) {
        const fresh = answer.slice(this.consumed)
        this.consumed = answer.length
        for (const sentence of this.splitter.push(fresh)) this.enqueue(sentence, turn)
        this.set({ reply: answer })
      }
      if (view.done && !this.runDone) {
        this.runDone = true
        for (const sentence of this.splitter.flush()) this.enqueue(sentence, turn)
        this.unwatch?.()
        this.unwatch = null
        if (view.failed) this.set({ error: 'call.replyFailed' })
        this.maybeDone(turn)
      }
    }
    this.unwatch = this.deps.watchRun(runId, update)
    update()
  }

  /**
   * Seslendirme SIRAYLA isteniyor (her biri oncekinin dilini kullaniyor):
   * kisa bir cumlenin dili yanlis tahmin edilip yanitin ortasinda ses
   * degismesin. Calma ise bir onceki cumle calarken sonrakinin hazirlanmasina
   * izin veriyor.
   */
  private enqueue(sentence: string, turn: number): void {
    // Yalnizca koddan olusan parca: sunucu kod bloklarini okumuyor, istek
    // bos metin hatasiyla donerdi.
    if (!sentence.replace(/```[\s\S]*?```/g, '').trim()) return
    const job = this.chain.then(async () => {
      if (turn !== this.turn) return null
      try {
        const result = await this.deps.tts(sentence, this.lang)
        if (!this.lang) this.lang = result.lang
        return result.audio
      } catch {
        return null
      }
    })
    this.chain = job
    this.audio.push(job)
    void this.pump(turn)
  }

  private async pump(turn: number): Promise<void> {
    if (this.pumping) return
    this.pumping = true
    try {
      while (this.audio.length && turn === this.turn) {
        const clip = await this.audio.shift()!
        if (turn !== this.turn) break
        if (!clip) continue
        this.set({ state: 'speaking' })
        await this.deps.player.play(clip).catch(() => undefined)
      }
    } finally {
      this.pumping = false
    }
    // Soz kesildiyse yeni turun kuyrugu eski dongu bitene kadar bekledi.
    if (this.audio.length && this.snap.state !== 'ended') void this.pump(this.turn)
    else this.maybeDone(turn)
  }

  private maybeDone(turn: number): void {
    if (turn !== this.turn || !this.runDone || this.pumping || this.audio.length) return
    if (this.snap.state === 'ended') return
    this.listen()
  }
}
